import { isNil, get, findKey } from 'lodash';
import { CONNECTOR_TYPES, CONNECTOR_SUBTYPES } from './Connectors';

/**
 * Gets the connector id.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The id for the connector or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getId = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, '_id', '');
};

/**
 * Get connector name.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The name of the connector or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getName = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'name', '');
};

/**
 * Gets the connector type.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector type or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getType = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'type', '');
};

/**
 * Gets the connector subtype.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector subtype or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getSubType = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'subtype', '');
};

/**
 * Gets the Enum type.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The enum type or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getEnumType = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return findKey(CONNECTOR_TYPES, type => type === getType(connectorObject));
};

/**
 * Gets the connector IP.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector IP or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getIp = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'ip', '');
};

/**
 * Gets the connector port.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector port or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getPort = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'port', '');
};

/**
 * Gets the connector user.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector user or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getUser = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'user', '');
};

/**
 * Gets the connector password.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector password or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getPassword = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'password', '');
};

/**
 * Gets the connector domain.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector domain or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getDomain = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'domain', '');
};

/**
 * Gets the connector database name.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector database name or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getDatabaseName = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'databaseName', '');
};

/**
 * Gets the name of the schema.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The name of the schema or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getSchema = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'schema', 'public');
};

/**
 * Checks if the connector has IP.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector has ip, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const hasIp = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'ip', '') !== '';
};

/**
 * Checks if the connector has domain.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector has domain, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const hasDomain = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'domain', '') !== '';
};

/**
 * Checks if the connector is FTP.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is FTP, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isFTP = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getType(connectorObject) === CONNECTOR_TYPES.FTP;
};

/**
 * Checks if the connector is HTTP.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is HTTP, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isHTTP = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getType(connectorObject) === CONNECTOR_TYPES.HTTP;
};

/**
 * Checks if the connector is PostgreSQL.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is PostgreSQL, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isPostgreSQL = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getType(connectorObject) === CONNECTOR_TYPES.POSTGRESQL;
};

/**
 * Checks if the connector is GeoJSONVirtual.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is GeoJSONVirtual, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isGeoJSONVT = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getType(connectorObject) === CONNECTOR_TYPES.GEOJSONVT;
};

/**
 * Checks if the connector is WMS.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is WMS, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isWMS = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getType(connectorObject) === CONNECTOR_TYPES.WMS;
};

/**
 * Checks if the connector is Vector.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is Vector, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isVector = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getSubType(connectorObject) === CONNECTOR_SUBTYPES.VECTOR;
};

/**
 * Checks if the connector is Thematic.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is Thematic, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isThematic = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getSubType(connectorObject) === CONNECTOR_SUBTYPES.THEMATIC;
};

/**
 * Checks if the connector is Raster.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is Raster, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isRaster = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getSubType(connectorObject) === CONNECTOR_SUBTYPES.RASTER;
};

/**
 * Checks if the connector is PointCloud.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is PointCloud, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isPointCloud = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getSubType(connectorObject) === CONNECTOR_SUBTYPES.POINTCLOUD;
};

/**
 * Checks if the connector is Generic.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is Generic, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isGeneric = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return getSubType(connectorObject) === CONNECTOR_SUBTYPES.GENERIC;
};

/**
 * Checks if the connector is deleted.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is in trash, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isDeleted = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'deleted', '') !== 'no';
};

/**
 * Gets the connector fileType.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector fileType or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getFileType = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'fileType', '');
};

/**
 * Gets the connector filePath.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} The connector filePath or empty string if not found.
 * @throws {Error} If connector is null or undefined.
 */
export const getFilePath = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'filePath', '');
};

/**
 * Checks if the connector is output.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {boolean} True if the connector is output, false otherwise.
 * @throws {Error} If connector is null or undefined.
 */
export const isOutput = connectorObject => {
  if (isNil(connectorObject)) throw new Error('connection not defined');
  return get(connectorObject, 'isOutput', false);
};
